import { useState, useEffect, useCallback } from 'react';
import {
  CheckSquare, Plus, ChevronDown, ChevronRight, Check,
  Trash2, Clock, AlertTriangle, X
} from 'lucide-react';

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const PRIORITY_COLORS = {
  haute: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Haute' },
  moyenne: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Moyenne' },
  basse: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Basse' }
};

const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date(new Date().toDateString());
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch { return dateStr; }
};

// ─── Quick Add Form ────────────────────────────────────────
const QuickAddForm = ({ onClose, onAdd }) => {
  const [form, setForm] = useState({ titre: '', description: '', priority: 'moyenne', deadline: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.titre.trim()) return;
    setSaving(true);
    await onAdd(form);
    setSaving(false);
    onClose();
  };

  return (
    <div className="space-y-2.5" data-testid="todo-quick-create">
      <input
        data-testid="todo-quick-title"
        value={form.titre}
        onChange={e => setForm({ ...form, titre: e.target.value })}
        className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
        placeholder="Titre de la tache..."
        autoFocus
        onKeyDown={e => e.key === 'Enter' && submit()}
      />
      <textarea
        data-testid="todo-quick-desc"
        value={form.description}
        onChange={e => setForm({ ...form, description: e.target.value })}
        className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs placeholder-slate-500 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
        rows={2}
        placeholder="Description (optionnel)..."
      />
      <div className="flex gap-2">
        <select
          data-testid="todo-quick-priority"
          value={form.priority}
          onChange={e => setForm({ ...form, priority: e.target.value })}
          className="flex-1 p-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs"
        >
          <option value="basse">Basse</option>
          <option value="moyenne">Moyenne</option>
          <option value="haute">Haute</option>
        </select>
        <input
          data-testid="todo-quick-deadline"
          type="date"
          value={form.deadline}
          onChange={e => setForm({ ...form, deadline: e.target.value })}
          className="flex-1 p-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs"
        />
      </div>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-xs">
          Annuler
        </button>
        <button
          data-testid="todo-quick-submit"
          onClick={submit}
          disabled={saving || !form.titre.trim()}
          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-white text-xs font-medium"
        >
          {saving ? '...' : 'Ajouter'}
        </button>
      </div>
    </div>
  );
};

// ─── Main Widget ───────────────────────────────────────────
export default function MaTodoListWidget({ authUser }) {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const userEmail = authUser?.email || '';

  const loadTodos = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const r = await authFetch(`${API}/user/espace?user_email=${encodeURIComponent(userEmail)}`);
      if (r.ok) {
        const d = await r.json();
        setTodos(d.todos || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [userEmail]);

  useEffect(() => { loadTodos(); }, [loadTodos]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(loadTodos, 60000);
    return () => clearInterval(interval);
  }, [loadTodos]);

  const pendingTodos = todos.filter(t => !t.done);
  const pendingCount = pendingTodos.length;

  // Sort: haute first, then moyenne, then basse
  const priorityOrder = { haute: 0, moyenne: 1, basse: 2 };
  const sortedTodos = [...pendingTodos].sort((a, b) =>
    (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2)
  );

  const addTodo = async (form) => {
    try {
      const r = await authFetch(`${API}/user/espace/todo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, user_email: userEmail })
      });
      if (r.ok) loadTodos();
    } catch (e) { console.error(e); }
  };

  const toggleTodo = async (todoId) => {
    try {
      await authFetch(`${API}/user/espace/todo/${todoId}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail })
      });
      loadTodos();
    } catch (e) { console.error(e); }
  };

  const deleteTodo = async (todoId) => {
    try {
      await authFetch(`${API}/user/espace/todo/${todoId}?user_email=${encodeURIComponent(userEmail)}`, { method: 'DELETE' });
      loadTodos();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="bg-slate-800/80 border border-slate-700 rounded-xl overflow-hidden" data-testid="todo-widget">
      {/* Header */}
      <button
        onClick={() => { setIsExpanded(!isExpanded); setShowCreate(false); }}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
        data-testid="todo-widget-toggle"
      >
        <div className="flex items-center gap-2">
          {isExpanded
            ? <ChevronDown className="w-4 h-4 text-slate-400" />
            : <ChevronRight className="w-4 h-4 text-slate-400" />
          }
          <CheckSquare className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-semibold text-white">Ma Todo List</span>
          {pendingCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center" data-testid="todo-pending-badge">
              {pendingCount}
            </span>
          )}
        </div>
        <span className="text-xs text-slate-500">{pendingCount} en cours</span>
      </button>

      {/* Body */}
      {isExpanded && (
        <div className="px-3 pb-3">
          {showCreate ? (
            <QuickAddForm onClose={() => setShowCreate(false)} onAdd={addTodo} />
          ) : (
            <>
              {/* Quick add button */}
              <button
                data-testid="todo-add-btn"
                onClick={() => setShowCreate(true)}
                className="w-full mb-3 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg flex items-center justify-center gap-2 text-blue-400 text-xs font-medium transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Nouvelle tache
              </button>

              {/* Todos list */}
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : sortedTodos.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">Aucune tache en attente</p>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {sortedTodos.map(todo => {
                    const prio = PRIORITY_COLORS[todo.priority] || PRIORITY_COLORS.moyenne;
                    const overdue = isOverdue(todo.deadline);

                    return (
                      <div
                        key={todo.id}
                        data-testid={`todo-item-${todo.id}`}
                        className={`p-2.5 rounded-lg border transition-all ${
                          overdue
                            ? 'bg-red-500/5 border-red-500/20'
                            : 'bg-slate-900/40 border-slate-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {/* Toggle button */}
                          <button
                            data-testid={`todo-toggle-${todo.id}`}
                            onClick={() => toggleTodo(todo.id)}
                            className="w-4.5 h-4.5 mt-0.5 rounded-full border-2 border-blue-400 hover:bg-blue-500/20 flex items-center justify-center shrink-0 transition-colors"
                          >
                          </button>

                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-white truncate">{todo.titre}</p>
                            {todo.description && (
                              <p className="text-xs text-slate-500 truncate mt-0.5">{todo.description}</p>
                            )}
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              <span className={`text-xs px-1.5 py-0.5 rounded ${prio.bg} ${prio.text}`}>{prio.label}</span>
                              {todo.deadline && (
                                <span className={`text-xs flex items-center gap-0.5 ${overdue ? 'text-red-400' : 'text-slate-500'}`}>
                                  <Clock className="w-2.5 h-2.5" />
                                  {formatDate(todo.deadline)}
                                  {overdue && <AlertTriangle className="w-2.5 h-2.5 ml-0.5" />}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Delete */}
                          <button
                            data-testid={`todo-delete-${todo.id}`}
                            onClick={() => deleteTodo(todo.id)}
                            className="p-1 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded shrink-0 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
